// Any fixed origin works: only whether a value stays on it matters.
const BASE = 'https://return.invalid'

/**
 * Sign-in itself: the magic-link route redeems a token from its URL, so a return path pointing there
 * could sign the user into someone else's account. Routes match case-insensitively on the decoded
 * path, so the check does too, and a path that doesn't decode is refused.
 */
function isSignInRoute(pathname: string): boolean {
  try {
    return /^\/(?:t|login)(?:\/|$)/i.test(decodeURIComponent(pathname))
  } catch {
    return true
  }
}

/**
 * The same-origin path to send someone to after sign-in, or '/'. A prefix
 * check alone is not enough: browsers read `\` as `/` and drop tabs and
 * newlines, so `/\evil.example` and `/\t/evil.example` both turn into the
 * scheme-relative `//evil.example`. Resolving the value catches those, and the
 * resolved path must not start with `//` either, because dot segments
 * normalize too: `/.//evil.example` comes out as `//evil.example`. It must not
 * lead back into sign-in either.
 */
export function getSafeReturnPath(value: string | null): string {
  if (!value?.startsWith('/')) return '/'
  try {
    const url = new URL(value, BASE)
    if (url.origin !== BASE || url.pathname.startsWith('//') || isSignInRoute(url.pathname)) return '/'
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return '/'
  }
}

/** `/login`, carrying where to go back to unless that is the dashboard's front door. */
export function loginPathReturningTo(path: string): string {
  const returnPath = getSafeReturnPath(path)
  return returnPath === '/' ? '/login' : `/login?returnTo=${encodeURIComponent(returnPath)}`
}
